// Shared helpers for the Inventory & Rentals module. Dates travel as YYYY-MM-DD strings
// end-to-end (string compare == date compare), matching the backend.
import { costBeforeVat, stripVat } from '../../../../shared/vat.js';

export function heDay(s) {
    if (!s) return '';
    const p = String(s).slice(0, 10).split('-');
    return p[2] + '.' + p[1];
}

export function heDate(s) {
    if (!s) return '';
    const p = String(s).slice(0, 10).split('-');
    return p[2] + '.' + p[1] + '.' + p[0].slice(2);
}

export function money(v) {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    if (isNaN(n)) return '';
    return '₪' + n.toLocaleString();
}

export function todayStr() {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

// Normalize anything the app calls a date into the YYYY-MM-DD this module speaks.
// The customer page hands us Date OBJECTS (EventHandler.prepEventFromServerToVue converts
// event.date on load), and String(dateObject).slice(0,10) yields 'Sat Aug 2' — which silently
// empties <input type="date"> and breaks the pickup/return defaults derived from it.
export function toYmd(d) {
    if (!d) return null;
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
    const dt = (d instanceof Date) ? d : new Date(d);
    if (isNaN(dt.getTime())) return null;
    return dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + String(dt.getDate()).padStart(2, '0');
}

// The app-wide <DatePicker> (flatpickr) reads and writes d/m/Y, this module and the API
// speak YYYY-MM-DD — these two bridge the gap for the v-model proxies.
export function ymdToDmy(d) {
    const ymd = toYmd(d);
    if (!ymd) return '';
    const p = ymd.split('-');
    return p[2] + '/' + p[1] + '/' + p[0];
}

export function dmyToYmd(s) {
    if (!s) return '';
    const p = String(s).split('/');
    if (p.length !== 3) return '';
    return p[2] + '-' + p[1].padStart(2, '0') + '-' + p[0].padStart(2, '0');
}

export function addDays(s, n) {
    const p = s.split('-').map(Number);
    const d = new Date(p[0], p[1] - 1, p[2] + n);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

export function diffDays(a, b) {
    const p = a.split('-').map(Number);
    const q = b.split('-').map(Number);
    return Math.round((new Date(q[0], q[1] - 1, q[2]).getTime() - new Date(p[0], p[1] - 1, p[2]).getTime()) / 86400000);
}

// The rental around an event: pickup two days before it, return three days after - the rental form's default
// (RentalModal) and what the catalogue's presentation asks "free for the event on this day" with
export function eventRentalWindow(eventDate) {
    const day = toYmd(eventDate);
    if (!day) return null;
    return { from: addDays(day, -2), to: addDays(day, 3) };
}

// "22/10/2026 בשעה 14:32" for the api's UTC datetimes (shown in the browser's time) - the אישור איסוף lines
export function whenText(value) {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return p(d.getDate()) + '/' + p(d.getMonth() + 1) + '/' + d.getFullYear() + ' בשעה ' + p(d.getHours()) + ':' + p(d.getMinutes());
}

export function waLink(phone) {
    if (!phone) return null;
    let digits = String(phone).replace(/\D/g, '');
    if (digits.indexOf('0') === 0) digits = '972' + digits.slice(1);
    return 'https://wa.me/' + digits;
}

// One status language across every screen (catalogue dot, board column, journey entry)
export const STATE_META = {
    free:     { label: 'בסטודיו',     cls: 'free' },
    outSoon:  { label: 'יוצאת בקרוב', cls: 'res' },
    reserved: { label: 'שמורה',       cls: 'res' },
    prep:     { label: 'בהכנה',       cls: 'res' },
    out:      { label: 'אצל לקוחה',   cls: 'out' },
    overdue:  { label: 'באיחור',      cls: 'over' },
    cleaning: { label: 'בניקוי',      cls: 'clean' },
    sold:     { label: 'נמכרה',       cls: 'sold' },
    retired:  { label: 'נגרעה',       cls: 'sold' },
    custom:   { label: '',            cls: 'plain' },
};

// הגדרות → מלאי והשכרות: the deal milestone at which a copy quoted into a deal becomes שמורה /
// נמכר (business_setting.inventoryReserveOn — mirrors InventoryService.RESERVE_ON; null = orderSigned).
// `when` is the phrase the deal tab / modals finish their sentences with ("ישוריין …").
export const RESERVE_ON_DEFAULT = 'orderSigned';
export const RESERVE_ON_OPTIONS = [
    { value: 'quote',       label: 'מיד עם ההוספה לעסקה',   hint: 'בשמירת פרטי העסקה או ביצירת הצעת מחיר',            when: 'מיד עם השמירה בעסקה' },
    { value: 'order',       label: 'כשנוצרת הזמנה',         hint: 'הפקת מסמך הזמנה ללקוח (גם אם טרם נחתמה)',           when: 'כשתופק הזמנה' },
    { value: 'orderSigned', label: 'כשההזמנה נחתמת',        hint: 'סטטוס "הזמנה חתומה" — ברירת המחדל',                  when: 'כשההזמנה תיחתם' },
    { value: 'payment',     label: 'כשמתקבל תשלום',         hint: 'קבלה או חשבונית מס קבלה עם תשלום (גם מקדמה)',        when: 'כשיתקבל תשלום' },
    { value: 'invoice',     label: 'כשמופקת חשבונית',       hint: 'חשבונית מס או חשבונית מס קבלה',                      when: 'כשתופק חשבונית' },
];
export function reserveOnMeta(value) {
    return RESERVE_ON_OPTIONS.find(o => o.value === value) || RESERVE_ON_OPTIONS.find(o => o.value === RESERVE_ON_DEFAULT);
}

// Pill + label for a RENTAL row (history tables); overdue is derived, never stored
export function rentalStatusMeta(rental, today) {
    if (rental.status === 'out' && today && rental.dueBackAt && rental.dueBackAt < today)
        return { label: 'באיחור', pill: 'inv-pill-bad' };
    const map = {
        // picked into a deal that has not reached the business's reserve milestone yet (הזמנה
        // חתומה by default — RESERVE_ON_OPTIONS) — blocks nothing until it does
        quoted:    { label: 'הוצעה בהצעת מחיר', pill: 'inv-pill-quote' },
        reserved:  { label: 'שמורה',  pill: 'inv-pill-warn' },
        out:       { label: 'בחוץ',   pill: 'inv-pill-info' },
        returned:  { label: 'הוחזרה', pill: 'inv-pill-ok' },
        cancelled: { label: 'בוטלה',  pill: 'inv-pill-plain' },
    };
    return map[rental.status] || { label: rental.status || '', pill: 'inv-pill-plain' };
}

// The money pill of a rental / sale line, from the server's `payment` (InventoryService
// .annotatePayments): its income is what it was paid before PlanUp (`before` — the imported
// history, a deposit taken in the previous system) + its share of the חשבוניות מס / חשבוניות מס
// קבלה PlanUp issued on its event (`invoiced`). A pill only when there is income — a line with
// neither says nothing, never a "צפוי" (user rules 2026-09-15 / 2026-10-13). null also for no money
// on the line or a row whose status already says it (a quote, a cancelled row).
export function paymentMeta(row) {
    const p = row && row.payment;
    if (!p || !p.total || row.status === 'cancelled' || row.status === 'quoted') return null;
    // an API from before the split has no `invoiced`: its `received` was the invoiced share
    const invoiced = p.invoiced != null ? Number(p.invoiced) || 0 : Number(p.received) || 0;
    const before = Number(p.before) || 0;
    if (!invoiced && !before) return null;
    const state = invoiced + before >= p.total ? 'received' : 'partial';
    const pill = state === 'received' ? 'inv-pill-ok' : 'inv-pill-warn';
    const beforeTitle = 'שולמו ' + money(before) + ' לפני PlanUp (במערכת הקודמת)';
    const invoiceTitle = state === 'received' && !before
        ? 'הוצאה חשבונית מס / חשבונית מס קבלה על העסקה'
        : 'על העסקה הוצאו חשבוניות בסך ' + money(p.eventInvoiced) + ' מתוך ' + money(p.eventTotal) + ' — החלק היחסי של הפריט';
    if (!before) {
        return { state, pill, title: invoiceTitle, label: state === 'received' ? 'חשבונית' : 'חשבונית על ' + money(invoiced) };
    }
    if (!invoiced) {
        return { state, pill, title: beforeTitle, label: state === 'received' ? 'שולם לפני PlanUp' : money(before) + ' לפני PlanUp' };
    }
    return {
        state, pill, title: beforeTitle + ' · ' + invoiceTitle,
        label: state === 'received' ? 'שולם: לפני PlanUp + חשבונית' : 'שולמו ' + money(before + invoiced),
    };
}

// Dates of a quote (הצעת מחיר) for the note beside a copy: a sale quote is a single day,
// a rent quote is the pickup→return window (no buffers — a quote has none)
export function quoteDates(q) {
    if (!q) return '';
    if (q.dealType === 'sale') return heDay(q.eventDate || q.pickupPlannedAt);
    const from = q.pickupPlannedAt ? String(q.pickupPlannedAt).slice(0, 10) : '';
    const to = q.dueBackAt ? String(q.dueBackAt).slice(0, 10) : '';
    if (!from) return '';
    if (!to || to === from) return heDay(from);
    return heDay(from) + '\u00a0עד\u00a0' + heDay(to); // one unbreakable unit when the note wraps
}

// "There is a הצעת מחיר on this copy for these dates" — written NEXT TO a copy that stays
// available (a quote blocks nothing; the first deal to close gets the item)
export function quoteNote(q) {
    const who = q.customerName || 'לקוחה';
    const dates = quoteDates(q);
    return (q.dealType === 'sale' ? 'הוצעה בהצעת מחיר למכירה ל' : 'הוצעה בהצעת מחיר ל') + who + (dates ? ' · ' + dates : '');
}

// Human status line for a unit, built from the backend's derived `now` + `nextRental`
export function unitStatusLine(unit) {
    const now = unit.now || { state: 'free' };
    if (now.state === 'out') return 'אצל ' + (now.customerName || 'לקוחה') + ' · חוזרת ' + heDay(now.dueBackAt);
    if (now.state === 'overdue') return 'באיחור ' + now.daysLate + ' ' + (now.daysLate === 1 ? 'יום' : 'ימים') + ' · ' + (now.customerName || '');
    if (now.state === 'cleaning') return 'בניקוי עד ' + heDay(now.until);
    if (now.state === 'sold') return 'נמכרה ' + heDate(now.soldAt) + (now.soldToName ? ' ל' + now.soldToName : '');
    if (now.state === 'retired') return 'נגרעה מהמלאי' + (now.retireReason ? ' · ' + now.retireReason : '');
    if (unit.nextRental) return 'פנויה · שמורה ל' + (unit.nextRental.customerName || '') + ' מ־' + heDay(unit.nextRental.pickupPlannedAt);
    return 'בסטודיו — פנויה';
}

// Status line for the DATE VIEW (מצב בתאריך): unit.now here is the server's planned
// whereabouts for the chosen date, so 'reserved'/'prep' exist and free means free THAT day.
export function unitStatusLineOn(unit) {
    const now = unit.now || { state: 'free' };
    if (now.state === 'out') {
        if (now.returnedAt) return 'אצל ' + (now.customerName || 'לקוחה') + ' · הוחזרה ' + heDay(now.returnedAt);
        return 'אצל ' + (now.customerName || 'לקוחה') + (now.dueBackAt ? ' · חוזרת ' + heDay(now.dueBackAt) : '');
    }
    if (now.state === 'reserved') return 'שמורה ל' + (now.customerName || 'לקוחה') + ' · ' + heDay(now.pickupPlannedAt) + ' עד ' + heDay(now.dueBackAt);
    if (now.state === 'prep') return 'בהכנה לקראת ' + (now.customerName || 'לקוחה') + ' · איסוף ' + heDay(now.pickupPlannedAt);
    if (now.state === 'cleaning') return 'בניקוי עד ' + heDay(now.until);
    if (now.state === 'sold') return 'נמכרה ' + heDate(now.soldAt) + (now.soldToName ? ' ל' + now.soldToName : '');
    if (now.state === 'retired') return 'נגרעה מהמלאי' + (now.retireReason ? ' · ' + now.retireReason : '');
    if (unit.nextRental) return 'פנויה · שמורה ל' + (unit.nextRental.customerName || '') + ' מ־' + heDay(unit.nextRental.pickupPlannedAt);
    return 'פנויה';
}

// ---- VAT words (user, 22/10/2026: the profit of the rentals is written WITHOUT VAT everywhere and
// says so, and every cost says whether it is with or without VAT) ----
// costBeforeVat(amount, vatIncluded, date, osekPatur): a typed cost as the profit counts it — the
// same rule the server applies (shared/vat.js), so the form's total matches the saved purchaseCost
export { costBeforeVat, stripVat };
export const VAT_INCLUDED_LABEL = 'כולל מע״מ';
export const NO_VAT_LABEL = 'ללא מע״מ';
export function vatLabel(vatIncluded) { return vatIncluded ? VAT_INCLUDED_LABEL : NO_VAT_LABEL; }
// an עוסק פטור has no VAT on either side: the screens then say nothing about VAT and strip none
export function isOsekPatur(store) {
    return !!(store && store.state && store.state.businessSettings && store.state.businessSettings.osekPatur);
}

export function firstPhotoUrl(item) {
    if (item && item.photos && item.photos.length && item.photos[0] && item.photos[0].url) return item.photos[0].url;
    return null;
}

// axios_api_v2's global interceptor does NOT reject with the axios error: it rejects with
// error.response itself, or with a plain {message, statusCode} for 400/404/500. Normalize.
export function apiErr(err) {
    if (err && typeof err.status !== 'undefined' && err.data) {
        return { status: err.status, data: err.data, message: err.data.message || '' };
    }
    if (err && err.statusCode) {
        return { status: err.statusCode, data: null, message: err.message || '' };
    }
    return { status: 0, data: null, message: (err && err.message) || '' };
}
